import React, {useState, useMemo} from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tooltip from "@mui/material/Tooltip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import {Helmet} from "react-helmet";
import {useNavigate} from "react-router";
import {useFormik} from "formik";
import * as Yup from "yup";
import {useTestimonials} from "../firebase/useTestimonials";
import {useCheckIfAdmin} from "../firebase/useCheckIfAdmin";
import {TestimonialCard} from "../testimonials/TestimonialCard";
import {UnauthorizedAccess} from "../app/UnauthorizedAccess";

const testimonialValidationSchema = Yup.object().shape({
    author: Yup.string().trim().required("Forfatter / kursholder er påkrevd"),
    quote: Yup.string().trim().min(10, "Sitatet må være minst 10 tegn").required("Sitattekst er påkrevd"),
    title: Yup.string().trim(),
    role: Yup.string().trim(),
    organization: Yup.string().trim(),
    order: Yup.number().typeError("Rekkefølge må være et tall").integer().min(1, "Minst 1"),
});

export const AdminTestimonials = () => {
    const navigate = useNavigate();
    const isAdmin = useCheckIfAdmin();

    const [searchQuery, setSearchQuery] = useState("");
    const [editItem, setEditItem] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [deleteItem, setDeleteItem] = useState(null);

    const {testimonials, saveTestimonial, removeTestimonial} = useTestimonials();

    const sortedTestimonials = useMemo(() => {
        if (!testimonials) return [];
        return [...testimonials].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }, [testimonials]);

    const handleMove = async (item, direction) => {
        if (!sortedTestimonials || sortedTestimonials.length < 2) return;
        const currentIndex = sortedTestimonials.findIndex((t) => t.id === item.id);
        if (currentIndex === -1) return;

        const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
        if (targetIndex < 0 || targetIndex >= sortedTestimonials.length) return;

        const targetItem = sortedTestimonials[targetIndex];

        // Persist swapped orders cleanly
        await saveTestimonial({...item, order: targetIndex + 1});
        await saveTestimonial({...targetItem, order: currentIndex + 1});
    };

    const filteredTestimonials = useMemo(() => {
        if (!testimonials) return [];
        if (!searchQuery.trim()) return testimonials;
        const q = searchQuery.toLowerCase();
        return testimonials.filter(
            (t) =>
                (t.author && t.author.toLowerCase().includes(q)) ||
                (t.title && t.title.toLowerCase().includes(q)) ||
                (t.organization && t.organization.toLowerCase().includes(q)) ||
                (t.role && t.role.toLowerCase().includes(q)) ||
                (t.quote && t.quote.toLowerCase().includes(q))
        );
    }, [testimonials, searchQuery]);

    const formik = useFormik({
        initialValues: {
            id: editItem?.id || "",
            title: editItem?.title || "",
            quote: editItem?.quote || "",
            author: editItem?.author || "",
            role: editItem?.role || "",
            organization: editItem?.organization || "",
            order: editItem?.order || (testimonials ? testimonials.length + 1 : 1),
            published: editItem?.published !== false,
        },
        enableReinitialize: true,
        validationSchema: testimonialValidationSchema,
        onSubmit: async (values, {resetForm}) => {
            await saveTestimonial(values);
            setIsDialogOpen(false);
            setEditItem(null);
            resetForm();
        },
    });

    if (!isAdmin) {
        return <UnauthorizedAccess requiredRole="administrator" />;
    }

    const handleOpenCreate = () => {
        setEditItem(null);
        formik.resetForm();
        setIsDialogOpen(true);
    };

    const handleOpenEdit = (item) => {
        setEditItem(item);
        setIsDialogOpen(true);
    };

    const handleDelete = async () => {
        if (deleteItem) {
            await removeTestimonial(deleteItem.id);
            setDeleteItem(null);
        }
    };

    return (
        <Box sx={{py: {xs: 3, md: 5}, bgcolor: "#FAF8F5", minHeight: "85vh"}}>
            <Helmet>
                <title>Administrer tilbakemeldinger | Bekymringsmestring</title>
            </Helmet>

            <Container maxWidth="lg">
                {/* Back button */}
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/me")}
                    sx={{
                        mb: 3,
                        color: "primary.main",
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": {bgcolor: "rgba(40, 82, 56, 0.08)"},
                    }}
                >
                    Tilbake til Min side
                </Button>

                {/* Header card */}
                <Card
                    elevation={0}
                    sx={{
                        p: {xs: 3, md: 4},
                        borderRadius: 3,
                        bgcolor: "background.paper",
                        border: "1px solid",
                        borderColor: "rgba(40, 82, 56, 0.12)",
                        mb: 4,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: {xs: "column", sm: "row"},
                            alignItems: {xs: "flex-start", sm: "center"},
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mb: 1}}>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: "50%",
                                        bgcolor: "rgba(40, 82, 56, 0.1)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "primary.main",
                                    }}
                                >
                                    <FormatQuoteIcon sx={{fontSize: 26}} />
                                </Box>
                                <Typography variant="h4" component="h1" sx={{fontWeight: 800, color: "primary.main", fontSize: {xs: "1.5rem", md: "1.85rem"}}}>
                                    Administrer tilbakemeldinger
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{color: "text.secondary", maxWidth: 650}}>
                                Administrer sitater og erfaringer fra kursholdere og helsepersonell som vises i erfaringer-seksjonen på forsiden.
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreate}
                            sx={{
                                fontWeight: 700,
                                textTransform: "none",
                                borderRadius: 2,
                                px: 3,
                                py: 1.25,
                                whiteSpace: "nowrap",
                                boxShadow: "none",
                                "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                            }}
                        >
                            Legg til erfaring
                        </Button>
                    </Box>

                    {/* Search bar */}
                    <Box sx={{mt: 3}}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Søk i sitater, forfattere eller kommuner..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{color: "text.secondary"}} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchQuery("")} aria-label="Tøm søk">
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                            sx={{
                                bgcolor: "#FAF8F5",
                                borderRadius: 2,
                                "& .MuiOutlinedInput-notchedOutline": {borderColor: "rgba(40, 82, 56, 0.15)"},
                            }}
                        />
                    </Box>
                </Card>

                {/* List of Testimonials */}
                <Stack spacing={2}>
                    {filteredTestimonials.length === 0 ? (
                        <Card
                            elevation={0}
                            sx={{
                                p: 5,
                                textAlign: "center",
                                borderRadius: 3,
                                bgcolor: "background.paper",
                                border: "1px dashed rgba(40, 82, 56, 0.2)",
                            }}
                        >
                            <Typography variant="body1" sx={{color: "text.secondary", mb: 2}}>
                                {searchQuery ? "Ingen tilbakemeldinger matcher søket." : "Ingen tilbakemeldinger funnet."}
                            </Typography>
                            <Button variant="outlined" color="primary" startIcon={<AddIcon />} onClick={handleOpenCreate} sx={{textTransform: "none", borderRadius: 2}}>
                                Opprett den første tilbakemeldingen
                            </Button>
                        </Card>
                    ) : (
                        filteredTestimonials.map((item) => {
                            const itemIndex = sortedTestimonials.findIndex((t) => t.id === item.id);
                            const isFirst = itemIndex <= 0;
                            const isLast = itemIndex === -1 || itemIndex >= sortedTestimonials.length - 1;

                            return (
                                <Card
                                    key={item.id}
                                    elevation={0}
                                    sx={{
                                        p: {xs: 2.5, sm: 3},
                                        borderRadius: 3,
                                        bgcolor: "background.paper",
                                        border: "1px solid",
                                        borderColor: "rgba(40, 82, 56, 0.1)",
                                        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                                        "&:hover": {
                                            borderColor: "primary.main",
                                            boxShadow: "0 6px 20px rgba(40, 82, 56, 0.06)",
                                        },
                                    }}
                                >
                                    <Box sx={{display: "flex", flexDirection: {xs: "column", sm: "row"}, justifyContent: "space-between", alignItems: {xs: "flex-start", sm: "center"}, gap: 2}}>
                                        <Box sx={{flexGrow: 1, minWidth: 0}}>
                                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap"}}>
                                                <Typography variant="subtitle1" sx={{fontWeight: 700, color: "primary.main"}}>
                                                    {item.author}
                                                </Typography>
                                                {item.organization && (
                                                    <Chip
                                                        icon={<LocationOnOutlinedIcon sx={{fontSize: "0.85rem !important"}} />}
                                                        label={item.organization}
                                                        size="small"
                                                        sx={{height: 22, fontSize: "0.725rem", fontWeight: 600}}
                                                    />
                                                )}
                                                {item.role && (
                                                    <Typography variant="caption" sx={{color: "text.secondary"}}>
                                                        ({item.role})
                                                    </Typography>
                                                )}
                                                <Chip
                                                    label={item.published !== false ? "Publisert" : "Kladd"}
                                                    size="small"
                                                    color={item.published !== false ? "success" : "default"}
                                                    variant="outlined"
                                                    sx={{height: 22, fontSize: "0.7rem", fontWeight: 600}}
                                                />
                                                {item.order && (
                                                    <Typography variant="caption" sx={{color: "text.disabled"}}>
                                                        Rekkefølge: {item.order}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {item.title && (
                                                <Typography variant="body2" sx={{fontWeight: 600, color: "text.primary", mb: 0.5}}>
                                                    «{item.title}»
                                                </Typography>
                                            )}

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "text.secondary",
                                                    fontStyle: "italic",
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                "{item.quote}"
                                            </Typography>
                                        </Box>

                                        {/* Action Buttons: Reordering, Editing, Deleting */}
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1, alignSelf: {xs: "flex-end", sm: "center"}}}>
                                            <Tooltip title={isFirst ? "Øverst i listen" : "Flytt opp"}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={isFirst}
                                                        onClick={() => handleMove(item, "up")}
                                                        aria-label={`Flytt tilbakemelding fra ${item.author} opp`}
                                                        sx={{
                                                            width: 44,
                                                            height: 44,
                                                            borderRadius: 2,
                                                            color: "primary.main",
                                                            border: "1px solid rgba(40, 82, 56, 0.2)",
                                                            "&:hover": {bgcolor: "rgba(40, 82, 56, 0.08)"},
                                                            "&.Mui-disabled": {borderColor: "rgba(0, 0, 0, 0.08)"},
                                                            "&:focus-visible": {
                                                                outline: "2px solid #285238",
                                                                outlineOffset: 2,
                                                            },
                                                        }}
                                                    >
                                                        <ArrowUpwardIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>

                                            <Tooltip title={isLast ? "Nederst i listen" : "Flytt ned"}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={isLast}
                                                        onClick={() => handleMove(item, "down")}
                                                        aria-label={`Flytt tilbakemelding fra ${item.author} ned`}
                                                        sx={{
                                                            width: 44,
                                                            height: 44,
                                                            borderRadius: 2,
                                                            color: "primary.main",
                                                            border: "1px solid rgba(40, 82, 56, 0.2)",
                                                            "&:hover": {bgcolor: "rgba(40, 82, 56, 0.08)"},
                                                            "&.Mui-disabled": {borderColor: "rgba(0, 0, 0, 0.08)"},
                                                            "&:focus-visible": {
                                                                outline: "2px solid #285238",
                                                                outlineOffset: 2,
                                                            },
                                                        }}
                                                    >
                                                        <ArrowDownwardIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>

                                            <Tooltip title="Rediger tilbakemelding">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenEdit(item)}
                                                    aria-label={`Rediger tilbakemelding fra ${item.author}`}
                                                    sx={{
                                                        width: 44,
                                                        height: 44,
                                                        borderRadius: 2,
                                                        color: "primary.main",
                                                        border: "1px solid rgba(40, 82, 56, 0.2)",
                                                        "&:hover": {bgcolor: "rgba(40, 82, 56, 0.08)"},
                                                        "&:focus-visible": {
                                                            outline: "2px solid #285238",
                                                            outlineOffset: 2,
                                                        },
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Slett tilbakemelding">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setDeleteItem(item)}
                                                    aria-label={`Slett tilbakemelding fra ${item.author}`}
                                                    sx={{
                                                        width: 44,
                                                        height: 44,
                                                        borderRadius: 2,
                                                        color: "error.main",
                                                        border: "1px solid rgba(211, 47, 47, 0.2)",
                                                        "&:hover": {bgcolor: "rgba(211, 47, 47, 0.08)"},
                                                        "&:focus-visible": {
                                                            outline: "2px solid #d32f2f",
                                                            outlineOffset: 2,
                                                        },
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Card>
                            );
                        })
                    )}
                </Stack>
            </Container>

            {/* Add / Edit Dialog */}
            <Dialog
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {borderRadius: 3, p: {xs: 1, sm: 2}},
                }}
            >
                <DialogTitle component="div" sx={{display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1}}>
                    <Typography variant="h6" component="h2" sx={{fontWeight: 700, color: "primary.main"}}>
                        {editItem ? "Rediger tilbakemelding" : "Legg til ny tilbakemelding"}
                    </Typography>
                    <IconButton size="small" onClick={() => setIsDialogOpen(false)} aria-label="Lukk">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <form onSubmit={formik.handleSubmit}>
                    <DialogContent dividers sx={{display: "flex", flexDirection: "column", gap: 2.5}}>
                        <TextField
                            name="author"
                            label="Forfatter / kursholder (navn)"
                            placeholder="f.eks. Hilde Harstveit Ljosland"
                            fullWidth
                            size="small"
                            value={formik.values.author}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.author && Boolean(formik.errors.author)}
                            helperText={formik.touched.author && formik.errors.author}
                        />

                        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, gap: 2}}>
                            <TextField
                                name="role"
                                label="Stilling / tittel"
                                placeholder="f.eks. Psykisk helsearbeider"
                                fullWidth
                                size="small"
                                value={formik.values.role}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                            <TextField
                                name="organization"
                                label="Kommune / arbeidssted"
                                placeholder="f.eks. Sortland kommune"
                                fullWidth
                                size="small"
                                value={formik.values.organization}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                        </Box>

                        <TextField
                            name="title"
                            label="Overskrift / nøkkeltema (valgfritt)"
                            placeholder="f.eks. Et integrativt og pedagogisk kurs"
                            fullWidth
                            size="small"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />

                        <TextField
                            name="quote"
                            label="Sitattekst"
                            placeholder="Skriv tilbakemeldingen eller sitatet her..."
                            multiline
                            rows={4}
                            fullWidth
                            value={formik.values.quote}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.quote && Boolean(formik.errors.quote)}
                            helperText={formik.touched.quote && formik.errors.quote}
                        />

                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2}}>
                            <TextField
                                name="order"
                                label="Visningsrekkefølge"
                                type="number"
                                size="small"
                                helperText="Kan også endres med pil opp/ned"
                                sx={{width: 220}}
                                value={formik.values.order}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        name="published"
                                        checked={formik.values.published}
                                        onChange={formik.handleChange}
                                        color="primary"
                                    />
                                }
                                label="Synlig på forsiden"
                            />
                        </Box>

                        {/* Live Preview */}
                        <Box sx={{mt: 1}}>
                            <Typography variant="caption" sx={{fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", mb: 1}}>
                                Forhåndsvisning:
                            </Typography>
                            <Box sx={{maxWidth: 480}}>
                                <TestimonialCard
                                    testimonial={{
                                        title: formik.values.title,
                                        quote: formik.values.quote || "Her vises sitatteksten...",
                                        author: formik.values.author || "Navn på kursholder",
                                        role: formik.values.role,
                                        organization: formik.values.organization,
                                    }}
                                />
                            </Box>
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{px: 3, py: 2}}>
                        <Button onClick={() => setIsDialogOpen(false)} color="inherit" sx={{textTransform: "none"}}>
                            Avbryt
                        </Button>
                        <Button type="submit" variant="contained" color="primary" sx={{textTransform: "none", borderRadius: 2, px: 3}}>
                            {editItem ? "Lagre endringer" : "Opprett tilbakemelding"}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={Boolean(deleteItem)}
                onClose={() => setDeleteItem(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{sx: {borderRadius: 3}}}
            >
                <DialogTitle sx={{fontWeight: 700}}>Bekreft sletting</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{color: "text.secondary"}}>
                        Er du sikker på at du vil slette tilbakemeldingen fra <strong>{deleteItem?.author}</strong>? Handlingen kan ikke angres.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2}}>
                    <Button onClick={() => setDeleteItem(null)} color="inherit" sx={{textTransform: "none"}}>
                        Avbryt
                    </Button>
                    <Button onClick={handleDelete} color="error" variant="contained" sx={{textTransform: "none", borderRadius: 2}}>
                        Slett
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
